/* ==========================================================================
   HODL Vault Header – Konzept C
   Mega-Panels mit Hover-Absicht, Tastaturbedienung, Mobilmenue,
   Schnellzugriff (Strg/Cmd+K oder "/") mit Shopify Predictive Search,
   Statuszeile und Scroll-Fortschritt. Ohne externe Bibliotheken.
   ========================================================================== */
(() => {
  if (window.VaultHeaderLoaded) return;
  window.VaultHeaderLoaded = true;

  const loadedScripts = {};
  const loadScript = (src) => {
    if (!src || loadedScripts[src]) return;
    loadedScripts[src] = true;
    const s = document.createElement('script');
    s.src = src;
    s.defer = true;
    document.head.appendChild(s);
  };

  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const focusables = (el) =>
    Array.from(el.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])')).filter(
      (n) => n.offsetParent !== null || n === document.activeElement
    );

  function init(root) {
    if (!root) return null;

    const ac = new AbortController();
    const on = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: ac.signal });
    const timers = new Set();
    const later = (fn, ms) => {
      const id = setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
      return id;
    };

    let config = {};
    try {
      config = JSON.parse(document.getElementById('VaultHeaderConfig').textContent);
    } catch (e) {
      config = {};
    }
    const strings = config.strings || {};
    const assets = config.assets || {};

    const bar = root.querySelector('[data-vh-bar]');
    const desktop = window.matchMedia('(min-width: 1200px)');

    /* --- Scroll-Zustand, Fortschritt, Header-Hoehe ---------------------- */
    const progress = root.querySelector('[data-vh-progress]');
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const y = window.scrollY || document.documentElement.scrollTop;
        root.toggleAttribute('data-scrolled', y > 8);
        if (progress) {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          progress.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max).toFixed(4) : 0})`;
        }
      });
    };
    on(window, 'scroll', onScroll, { passive: true });
    onScroll();

    const setHeights = () => {
      if (!bar) return;
      const h = Math.round(bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    };
    let resizeObserver = null;
    if (bar && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(setHeights);
      resizeObserver.observe(bar);
    }
    setHeights();

    /* --- Desktop-Navigation --------------------------------------------- */
    const nav = root.querySelector('[data-vh-nav]');
    const pill = root.querySelector('[data-vh-pill]');
    const panels = new Map();
    root.querySelectorAll('[data-vh-panel]').forEach((p) => panels.set(p.dataset.vhPanel, p));
    let openId = null;
    let openTimer = null;
    let closeTimer = null;

    const itemFor = (id) => (nav ? nav.querySelector(`[data-vh-item="${id}"]`) : null);

    const movePill = (item) => {
      if (!pill || !nav) return;
      if (!item) {
        nav.removeAttribute('data-pill');
        return;
      }
      const nr = nav.getBoundingClientRect();
      const ir = item.getBoundingClientRect();
      pill.style.width = `${ir.width}px`;
      pill.style.height = `${ir.height}px`;
      pill.style.top = `${ir.top - nr.top}px`;
      pill.style.transform = `translateX(${ir.left - nr.left}px)`;
      nav.setAttribute('data-pill', '');
    };

    const closeAll = () => {
      clearTimeout(openTimer);
      panels.forEach((p) => {
        p.removeAttribute('data-open');
        p.inert = true;
      });
      if (nav) {
        nav.querySelectorAll('.vh-nav__item.is-open').forEach((i) => {
          i.classList.remove('is-open');
          const t = i.querySelector('[data-vh-toggle]');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
      openId = null;
    };

    const openPanel = (id, focusFirst = false) => {
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      const panel = panels.get(id);
      const item = itemFor(id);
      if (!panel || !item) {
        closeAll();
        return;
      }
      if (openId !== id) {
        closeAll();
        openId = id;
        panel.inert = false;
        panel.setAttribute('data-open', '');
        item.classList.add('is-open');
        const t = item.querySelector('[data-vh-toggle]');
        if (t) t.setAttribute('aria-expanded', 'true');
        movePill(item);
      }
      if (focusFirst) {
        const first = panel.querySelector('a[href]');
        if (first) requestAnimationFrame(() => first.focus());
      }
    };

    const scheduleClose = (ms = 220) => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      closeTimer = later(() => {
        closeAll();
        movePill(null);
      }, ms);
    };

    if (nav) {
      nav.querySelectorAll('.vh-nav__item').forEach((item) => {
        const id = item.dataset.vhItem;
        on(item, 'pointerenter', (e) => {
          if (e.pointerType !== 'mouse' || !desktop.matches) return;
          clearTimeout(closeTimer);
          movePill(item);
          clearTimeout(openTimer);
          if (panels.has(id)) {
            openTimer = later(() => openPanel(id), openId ? 0 : 90);
          } else {
            openTimer = later(closeAll, 60);
          }
        });
        on(item, 'focusin', () => movePill(item));

        const toggle = item.querySelector('[data-vh-toggle]');
        if (toggle) {
          on(toggle, 'click', (e) => {
            if (openId === id) {
              closeAll();
              return;
            }
            openPanel(id, e.detail === 0);
          });
        }
      });

      on(nav, 'pointerleave', () => {
        if (!openId) movePill(null);
      });
    }

    if (bar) {
      on(bar, 'pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        if (openId) scheduleClose();
      });
      on(bar, 'pointerenter', () => clearTimeout(closeTimer));
    }

    panels.forEach((panel, id) => {
      on(panel, 'keydown', (e) => {
        if (e.key !== 'Tab') return;
        const items = focusables(panel);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        const item = itemFor(id);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          const t = item && item.querySelector('[data-vh-toggle]');
          if (t) t.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          closeAll();
          const next = item && item.nextElementSibling ? item.nextElementSibling.querySelector('a, button') : root.querySelector('.vh-actions button, .vh-actions a');
          if (next) next.focus();
        }
      });
    });

    on(document, 'click', (e) => {
      if (openId && bar && !bar.contains(e.target)) {
        closeAll();
        movePill(null);
      }
    });

    /* --- Mobilmenue ------------------------------------------------------ */
    const burger = root.querySelector('[data-vh-burger]');
    const burgerLabel = root.querySelector('[data-vh-burger-label]');
    const mobile = root.querySelector('[data-vh-mobile]');

    const setMobile = (open) => {
      if (!mobile || !burger) return;
      if (open) {
        const r = bar.getBoundingClientRect();
        root.style.setProperty('--vh-mobile-top', `${Math.max(0, Math.round(r.bottom))}px`);
        mobile.inert = false;
        mobile.setAttribute('data-open', '');
        document.documentElement.classList.add('vh-lock');
      } else {
        if (!mobile.hasAttribute('data-open')) return;
        mobile.removeAttribute('data-open');
        mobile.inert = true;
        if (!cmd || cmd.hidden) document.documentElement.classList.remove('vh-lock');
      }
      burger.setAttribute('aria-expanded', String(open));
      if (burgerLabel) burgerLabel.textContent = open ? 'Menü schliessen' : 'Menü öffnen';
    };

    if (burger) {
      on(burger, 'click', () => setMobile(burger.getAttribute('aria-expanded') !== 'true'));
    }

    if (mobile) {
      mobile.querySelectorAll('.vh-m__toggle').forEach((t) => {
        on(t, 'click', () => {
          const sub = document.getElementById(t.getAttribute('aria-controls'));
          const open = t.getAttribute('aria-expanded') !== 'true';
          t.setAttribute('aria-expanded', String(open));
          if (sub) sub.hidden = !open;
        });
      });
      on(mobile, 'keydown', (e) => {
        if (e.key !== 'Tab' || e.shiftKey) return;
        const items = focusables(mobile);
        if (items.length && document.activeElement === items[items.length - 1]) {
          e.preventDefault();
          burger.focus();
        }
      });
      on(mobile, 'click', (e) => {
        const link = e.target.closest('a[href]');
        if (link && link.getAttribute('href').includes('#')) setMobile(false);
      });
    }

    const onBreakpoint = () => {
      if (desktop.matches) setMobile(false);
      else closeAll();
    };
    on(desktop, 'change', onBreakpoint);

    /* --- Schnellzugriff / Suche ----------------------------------------- */
    const cmd = root.querySelector('[data-vh-cmd]');
    const input = cmd ? cmd.querySelector('[data-vh-cmd-input]') : null;
    const list = cmd ? cmd.querySelector('[data-vh-cmd-list]') : null;
    const status = cmd ? cmd.querySelector('[data-vh-cmd-status]') : null;
    const form = cmd ? cmd.querySelector('[data-vh-cmd-form]') : null;
    const quickHtml = list ? list.innerHTML : '';
    let lastFocus = null;
    let active = -1;
    let request = null;
    let debounce = null;

    const options = () => (list ? Array.from(list.querySelectorAll('[data-vh-opt]')) : []);

    const setActive = (index) => {
      const opts = options();
      if (!opts.length) {
        active = -1;
        input.removeAttribute('aria-activedescendant');
        return;
      }
      active = (index + opts.length) % opts.length;
      opts.forEach((o, k) => o.setAttribute('aria-selected', String(k === active)));
      input.setAttribute('aria-activedescendant', opts[active].id);
      opts[active].scrollIntoView({ block: 'nearest' });
    };

    const renderQuick = () => {
      if (!list) return;
      list.innerHTML = quickHtml;
      status.textContent = '';
      active = -1;
      input.removeAttribute('aria-activedescendant');
      options().forEach((o) => o.setAttribute('aria-selected', 'false'));
    };

    const thumb = (src) => {
      if (!src) return '';
      const sep = src.includes('?') ? '&' : '?';
      return `${src}${sep}width=96`;
    };

    const renderResults = (query, results) => {
      const products = (results && results.products) || [];
      const content = []
        .concat(((results && results.collections) || []).map((r) => ({ ...r, kind: 'Kategorie' })))
        .concat(((results && results.pages) || []).map((r) => ({ ...r, kind: 'Seite' })))
        .concat(((results && results.articles) || []).map((r) => ({ ...r, kind: 'Wissen' })));
      let n = 0;
      let html = '';
      const currency = config.currency || 'CHF';

      if (products.length) {
        html += `<p class="vh-cmd__group" role="presentation">${escapeHtml(strings.products || 'Produkte')}</p>`;
        products.forEach((p) => {
          n += 1;
          const img = p.image || (p.featured_image && p.featured_image.url);
          const price = p.price ? `${currency} ${p.price}` : '';
          html += `<a class="vh-cmd__opt" role="option" id="VhOpt-${n}" href="${escapeHtml(p.url)}" aria-selected="false" data-vh-opt>
            <span class="vh-cmd__ico">${img ? `<img src="${escapeHtml(thumb(img))}" alt="" width="42" height="42" loading="lazy">` : ''}</span>
            <span class="vh-cmd__txt"><span class="vh-cmd__t">${escapeHtml(p.title)}</span><span class="vh-cmd__d">${escapeHtml([p.vendor, price].filter(Boolean).join(' · '))}</span></span>
            <span class="vh-cmd__go" aria-hidden="true">›</span></a>`;
        });
      }

      if (content.length) {
        html += `<p class="vh-cmd__group" role="presentation">${escapeHtml(strings.content || 'Seiten und Wissen')}</p>`;
        content.slice(0, 6).forEach((r) => {
          n += 1;
          html += `<a class="vh-cmd__opt" role="option" id="VhOpt-${n}" href="${escapeHtml(r.url)}" aria-selected="false" data-vh-opt>
            <span class="vh-cmd__ico" aria-hidden="true">#</span>
            <span class="vh-cmd__txt"><span class="vh-cmd__t">${escapeHtml(r.title)}</span><span class="vh-cmd__d">${escapeHtml(r.kind)}</span></span>
            <span class="vh-cmd__go" aria-hidden="true">›</span></a>`;
        });
      }

      n += 1;
      const searchUrl = `${config.searchUrl || '/search'}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
      html += `<a class="vh-cmd__opt vh-cmd__opt--all" role="option" id="VhOpt-${n}" href="${escapeHtml(searchUrl)}" aria-selected="false" data-vh-opt>
        <span class="vh-cmd__ico" aria-hidden="true">↵</span>
        <span class="vh-cmd__txt"><span class="vh-cmd__t">${escapeHtml(strings.allResults || 'Alle Ergebnisse für')} „${escapeHtml(query)}“</span></span>
        <span class="vh-cmd__go" aria-hidden="true">›</span></a>`;

      list.innerHTML = html;
      const hits = products.length + content.length;
      status.textContent = hits ? `${hits} ${hits === 1 ? 'Treffer' : 'Treffer'}` : strings.noResults || '';
      setActive(0);
    };

    const search = (query) => {
      if (request) request.abort();
      request = new AbortController();
      status.textContent = strings.loading || '';
      const base = config.suggestUrl || '/search/suggest';
      const url =
        `${base}.json?q=${encodeURIComponent(query)}` +
        '&resources%5Btype%5D=product,collection,article,page' +
        '&resources%5Blimit%5D=6&resources%5Blimit_scope%5D=each' +
        '&resources%5Boptions%5D%5Bunavailable_products%5D=last';
      fetch(url, { signal: request.signal, headers: { Accept: 'application/json' } })
        .then((r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.json();
        })
        .then((data) => {
          if (input.value.trim() !== query) return;
          renderResults(query, data && data.resources && data.resources.results);
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          renderResults(query, {});
        });
    };

    const openCmd = () => {
      if (!cmd || !cmd.hidden) return;
      lastFocus = document.activeElement;
      setMobile(false);
      closeAll();
      cmd.hidden = false;
      document.documentElement.classList.add('vh-lock');
      input.value = '';
      renderQuick();
      requestAnimationFrame(() => input.focus());
    };

    const closeCmd = () => {
      if (!cmd || cmd.hidden) return;
      cmd.hidden = true;
      if (request) request.abort();
      if (!mobile || !mobile.hasAttribute('data-open')) document.documentElement.classList.remove('vh-lock');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    };

    if (cmd) {
      root.querySelectorAll('[data-vh-cmd-open]').forEach((b) => on(b, 'click', openCmd));
      cmd.querySelectorAll('[data-vh-cmd-close]').forEach((b) => on(b, 'click', closeCmd));

      on(input, 'input', () => {
        clearTimeout(debounce);
        const q = input.value.trim();
        if (!q) {
          if (request) request.abort();
          renderQuick();
          return;
        }
        debounce = later(() => search(q), 160);
      });

      on(input, 'keydown', (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActive(active + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActive(active - 1);
        } else if (e.key === 'Home' && active > -1) {
          e.preventDefault();
          setActive(0);
        } else if (e.key === 'End' && active > -1) {
          e.preventDefault();
          setActive(options().length - 1);
        } else if (e.key === 'Enter') {
          const opts = options();
          if (active > -1 && opts[active]) {
            e.preventDefault();
            window.location.href = opts[active].href;
          } else if (!input.value.trim()) {
            e.preventDefault();
          }
        }
      });

      on(cmd, 'keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          closeCmd();
          return;
        }
        if (e.key !== 'Tab') return;
        const panel = cmd.querySelector('.vh-cmd__panel');
        const items = focusables(panel);
        if (!items.length) return;
        if (e.shiftKey && document.activeElement === items[0]) {
          e.preventDefault();
          items[items.length - 1].focus();
        } else if (!e.shiftKey && document.activeElement === items[items.length - 1]) {
          e.preventDefault();
          items[0].focus();
        }
      });

      on(list, 'pointermove', (e) => {
        const opt = e.target.closest('[data-vh-opt]');
        if (!opt) return;
        const index = options().indexOf(opt);
        if (index !== active) setActive(index);
      });

      on(form, 'submit', (e) => {
        if (!input.value.trim()) e.preventDefault();
      });
    }

    /* --- Mobile-Dock der Vorlage: Menue und Suche auf den Vault-Header ----- */
    // Das Dock sucht header-drawer bzw. search-modal der alten Kopfzeile. Die
    // Klicks werden in der Capture-Phase abgefangen, bevor das Dock reagiert.
    on(
      document,
      'click',
      (e) => {
        const item = e.target.closest && e.target.closest('mobile-dock .dock__item[data-action="menu"], mobile-dock .dock__item[data-action="search"]');
        if (!item) return;
        e.preventDefault();
        e.stopPropagation();
        if (item.dataset.action === 'menu') {
          setMobile(!burger || burger.getAttribute('aria-expanded') !== 'true');
        } else {
          openCmd();
        }
      },
      { capture: true }
    );

    /* --- Globale Tasten ---------------------------------------------------- */
    on(document, 'keydown', (e) => {
      const key = (e.key || '').toLowerCase();
      if ((e.metaKey || e.ctrlKey) && key === 'k') {
        e.preventDefault();
        if (cmd && cmd.hidden) openCmd();
        else closeCmd();
        return;
      }
      const t = e.target;
      const typing = t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName));
      if (key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        openCmd();
        return;
      }
      if (e.key === 'Escape') {
        if (openId) {
          const item = itemFor(openId);
          closeAll();
          const t2 = item && item.querySelector('[data-vh-toggle]');
          if (t2) t2.focus();
        }
        if (mobile && mobile.hasAttribute('data-open')) {
          setMobile(false);
          if (burger) burger.focus();
        }
      }
    });

    const kbd = root.querySelector('[data-vh-kbd]');
    if (kbd) {
      const mac = /Mac|iPhone|iPad|iPod/i.test((navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent);
      kbd.textContent = mac ? '⌘ K' : 'Ctrl K';
    }

    /* --- Statuszeile -------------------------------------------------------- */
    const rail = root.querySelector('[data-vh-rail]');
    let railTimer = null;
    if (rail) {
      const msgs = Array.from(rail.children);
      if (msgs.length > 1) {
        let i = 0;
        let paused = false;
        rail.classList.add('is-ready');
        msgs[0].classList.add('is-active');
        on(rail, 'pointerenter', () => (paused = true));
        on(rail, 'pointerleave', () => (paused = false));
        on(rail, 'focusin', () => (paused = true));
        on(rail, 'focusout', () => (paused = false));
        railTimer = setInterval(() => {
          if (paused || document.hidden) return;
          const current = msgs[i];
          i = (i + 1) % msgs.length;
          current.classList.remove('is-active');
          current.classList.add('is-leaving');
          later(() => current.classList.remove('is-leaving'), 520);
          msgs[i].classList.add('is-active');
        }, 4200);
      }
    }

    /* --- Skripte der Vorlage bei Bedarf nachladen ------------------------ */
    const bindOnce = (selector, cb) => {
      document.querySelectorAll(selector).forEach((el) => {
        ['pointerenter', 'touchstart', 'focusin'].forEach((type) => on(el, type, cb, { once: true, passive: true, capture: true }));
      });
    };
    bindOnce('predictive-search, search-modal, [data-action="search"]', () => {
      loadScript(assets.searchModal);
      loadScript(assets.predictive);
    });
    bindOnce('#cart-icon-bubble, a[href$="/cart"]', () => loadScript(assets.cartRecommendations));
    on(
      document,
      'click',
      (e) => {
        if (e.target.closest && e.target.closest('product-form, [name="add"], add-to-cart')) loadScript(assets.cartRecommendations);
      },
      { capture: true, passive: true }
    );
    const idle = () => loadScript(assets.cartRecommendations);
    if ('requestIdleCallback' in window) requestIdleCallback(idle, { timeout: 5000 });
    else later(idle, 3500);

    return {
      root,
      open: openPanel,
      close: () => {
        closeAll();
        movePill(null);
      },
      destroy() {
        ac.abort();
        timers.forEach((id) => clearTimeout(id));
        clearInterval(railTimer);
        if (resizeObserver) resizeObserver.disconnect();
        document.documentElement.classList.remove('vh-lock');
      },
    };
  }

  let api = null;
  const start = () => {
    api = init(document.querySelector('[data-vault-header]'));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* Theme-Editor: neu aufbauen und gewaehlte Bloecke zeigen */
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-vault-header]');
    if (!root) return;
    if (api) api.destroy();
    api = init(root);
  });
  document.addEventListener('shopify:block:select', (e) => {
    if (!api || !api.root.contains(e.target)) return;
    const id = e.target.dataset && e.target.dataset.vhItem;
    if (id && window.matchMedia('(min-width: 1200px)').matches) api.open(id);
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    if (api && api.root.contains(e.target)) api.close();
  });
})();
